import React, { useEffect, useRef, useState } from 'react';
import type { PhoneCallDto, AccessSessionsResponse, SsoIdentityDto, SsoProvider, SsoProvidersDto } from '../../../../shared/types';
import { ask } from '../dialogs';
import { runSso, SsoLogo, SSO_LABEL } from '../sso';
import type { Ctx } from '../ctx';
import { Icon } from '../icons';
import { Avatar, ErrorBanner, Field, Panel, PasswordInput, Pill, Row, Toggle } from '../ui';

const ROLE_LABEL = { admin: 'Consulente', agent: 'Commerciale', client: 'Cliente' } as const;

/** Preferenze locali, aggiornamenti e account. */
export function SettingsPage({ ctx }: { ctx: Ctx }) {
  const { api, me, prefs, setPrefs } = ctx;
  const [autostart, setAutostart] = useState<boolean | null>(null);
  const [hello, setHello] = useState<{ status: string; message: string } | null>(null);
  const [helloBusy, setHelloBusy] = useState(false);
  // Accesso rapido dopo «Esci»: salvato su questo PC?
  const [quick, setQuick] = useState<{ email: string } | null>(null);
  const [updateMsg, setUpdateMsg] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwDone, setPwDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const isAdmin = me.user.role === 'admin';
  // Chi entra solo con Google (es. l'amministratore) non ha una password da cambiare
  const [passwordless, setPasswordless] = useState(false);
  const [calls, setCalls] = useState<PhoneCallDto[] | null>(null);

  useEffect(() => {
    if (isAdmin) api.get<PhoneCallDto[]>('/api/phone/calls?limit=6').then(setCalls).catch(() => setCalls([]));
  }, [api, isAdmin]);

  useEffect(() => {
    window.tt.getAutostart().then(setAutostart).catch(() => setAutostart(false));
    window.tt.quickGet().then(setQuick).catch(() => setQuick(null));
    window.tt.helloAvailability().then(setHello).catch(() => setHello({ status: 'error', message: 'Windows Hello non disponibile.' }));
  }, []);

  // Attivando lo sblocco biometrico si chiede subito una verifica, così si è sicuri che funzioni.
  const toggleHello = async (v: boolean) => {
    if (!v) return setPrefs({ ...prefs, helloLock: false });
    setHelloBusy(true);
    try {
      const r = await window.tt.helloVerify('Attiva lo sblocco di CRM TecnoSide');
      if (r.ok) setPrefs({ ...prefs, helloLock: true });
      else if (r.status !== 'canceled') setHello({ status: r.status, message: r.message });
    } finally {
      setHelloBusy(false);
    }
  };

  const toggleAutostart = async (v: boolean) => {
    setAutostart(v);
    await window.tt.setAutostart(v).catch(() => undefined);
  };

  const checkUpdates = async () => {
    setChecking(true);
    setUpdateMsg(null);
    try {
      const r = await window.tt.checkUpdates();
      if (r.status === 'dev') setUpdateMsg('Controllo disponibile solo nella versione installata.');
      else if (r.status === 'available') setUpdateMsg(`Versione ${r.version} in download: verrà installata alla chiusura.`);
      else if (r.status === 'latest') setUpdateMsg('Hai già l’ultima versione.');
      else setUpdateMsg(`Controllo non riuscito: ${r.message ?? 'errore'}`);
    } finally {
      setChecking(false);
    }
  };

  const changePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (next !== confirm) return setPwError('Le due password nuove non coincidono');
    setBusy(true);
    setPwError(null);
    try {
      await api.put('/api/auth/password', { currentPassword: cur, newPassword: next });
      // L'accesso con impronta resta valido: aggiorno la password salvata su questo PC
      if (quick && quick.email === me.user.email) await window.tt.quickSet({ serverUrl: api.baseUrl, email: me.user.email, password: next, name: me.user.name }).catch(() => false);
      setCur('');
      setNext('');
      setConfirm('');
      setPwDone(true);
      setTimeout(() => setPwDone(false), 3000);
    } catch (err) {
      setPwError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panels">
      <Panel title="Impostazioni" subtitle="Preferenze di questo PC">
        <Row>
          <Icon name="play" size={20} className="muted" />
          <div className="grow">
            <div className="title">Avvio automatico con Windows</div>
            <div className="sub">Parte nascosta nell'area di notifica, pronta a ricevere gli avvisi.</div>
          </div>
          <Toggle checked={!!autostart} disabled={autostart === null} onChange={toggleAutostart} />
        </Row>
        <Row>
          <Icon name="bell" size={20} className="muted" />
          <div className="grow">
            <div className="title">Avvisi di Windows</div>
            <div className="sub">Promemoria, prenotazioni, lavoro iniziato o finito.</div>
          </div>
          <Toggle checked={prefs.desktopNotifications} onChange={(v) => setPrefs({ ...prefs, desktopNotifications: v })} />
        </Row>
        <Row>
          <Icon name="fingerprint" size={20} className="muted" />
          <div className="grow">
            <div className="title">Sblocco con impronta o viso</div>
            <div className="sub">
              {helloBusy
                ? 'In attesa di Windows Hello…'
                : hello && hello.status !== 'available'
                  ? hello.message
                  : 'All’apertura e quando torni dopo 5 minuti: basta il dito, il viso o il PIN.'}
            </div>
          </div>
          <Toggle
            checked={prefs.helloLock}
            disabled={helloBusy || !hello || (hello.status !== 'available' && !prefs.helloLock)}
            onChange={toggleHello}
          />
        </Row>
        {/* Serve a chi entra con la password: chi entra solo con Google non ce l'ha, quindi non si mostra (23/10/2026) */}
        {hello?.status === 'available' && !(passwordless && !quick) && (
          <Row>
            <Icon name="key" size={20} className="muted" />
            <div className="grow">
              <div className="title">Accesso con impronta o viso</div>
              <div className="sub">
                {passwordless
                  ? 'Non serve più: entri con Google.'
                  : quick
                    ? `Dopo «Esci» si rientra con Windows Hello (${quick.email}).`
                    : 'Si attiva dalla schermata di accesso, entrando con email e password.'}
              </div>
            </div>
            <Toggle
              checked={!!quick}
              disabled={!quick}
              onChange={async (v) => {
                if (v) return;
                await window.tt.quickSet(null).catch(() => false);
                setQuick(null);
              }}
            />
          </Row>
        )}
        <Row>
          <Icon name="server" size={20} className="muted" />
          <div className="grow">
            <div className="title">Collegato a</div>
            <div className="sub">{api.baseUrl}</div>
          </div>
          <Pill tone={ctx.live ? 'good' : 'bad'} icon={ctx.live ? 'wifi' : 'wifiOff'}>
            {ctx.live ? 'in tempo reale' : 'riconnessione…'}
          </Pill>
        </Row>
        <Row>
          <Icon name="download" size={20} className="muted" />
          <div className="grow">
            <div className="title">Versione {ctx.version}</div>
            <div className="sub">{updateMsg ?? 'Gli aggiornamenti si scaricano da soli e si installano alla chiusura.'}</div>
          </div>
          <button type="button" className="btn small" onClick={checkUpdates} disabled={checking} title="Cerca subito se c’è una versione nuova">
            <Icon name="refresh" size={15} /> {checking ? 'Controllo…' : 'Controlla ora'}
          </button>
        </Row>
        {isAdmin && (
          <>
            <Row>
              <Icon name="phone" size={20} className="muted" />
              <div className="grow">
                <div className="title">Telefono Android</div>
                <div className="sub" title={`Con l’app le chiamate con i clienti, anche su WhatsApp, avviano e fermano il conteggio da sole. Sul telefono apri ${api.baseUrl.replace(/^https?:\/\//, '')}/telefono`}>
                  Con l’app, le chiamate con i clienti contano il tempo da sole.
                </div>
              </div>
              <a className="btn small" href={`${api.baseUrl}/telefono`} target="_blank" rel="noreferrer" title="Il file per installare l’app sul telefono Android">
                <Icon name="download" size={15} /> Scarica l’app
              </a>
            </Row>
            {calls && calls.length > 0 && (
              <div className="ag-list" style={{ marginTop: 4 }}>
                {calls.map((c) => (
                  <div key={c.id} className="ag-row" style={{ cursor: 'default' }}>
                    <Icon name="phone" size={15} className="muted" />
                    <span className="ag-title">
                      <b>{c.clientName ?? c.contactName ?? c.number ?? 'Numero nascosto'}</b>
                      <div className="muted small">
                        {c.channel === 'whatsapp' ? 'WhatsApp · ' : ''}{c.direction === 'in' ? 'Ricevuta' : 'Fatta'} · {new Date(c.createdAt).toLocaleString('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        {c.outcome ? ` · ${c.outcome}` : ''}
                      </div>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Panel>

      <Panel title="Account" subtitle="Chi sei e come entri nel CRM">
        <div className="card hstack">
          <Avatar name={me.user.name} navy size={52} />
          <div className="grow">
            <div style={{ fontSize: 17, fontWeight: 500 }}>{me.user.name}</div>
            <div className="muted small">{me.user.email}</div>
          </div>
          <Pill tone="info" icon="user">
            {ROLE_LABEL[me.user.role]}
            {me.clientName ? ` · ${me.clientName}` : ''}
          </Pill>
        </div>

        {isAdmin && <GoogleKeys ctx={ctx} />}

        <LinkedAccounts ctx={ctx} passwordless={passwordless} />

        <Sessions ctx={ctx} onPasswordless={setPasswordless} />

        {passwordless && isAdmin ? (
          <div className="card stack" style={{ gap: 4 }}>
            <div className="eyebrow mb">Password</div>
            <div className="small">Entri solo con Google: la password non c'è, quindi nessuno può entrare provando a indovinarla.</div>
          </div>
        ) : (
        <form onSubmit={changePassword} className="card stack" style={{ gap: 4 }}>
          <div className="eyebrow mb">Cambia password</div>
          <ErrorBanner message={pwError} onClose={() => setPwError(null)} />
          <Field label="Password attuale">
            <PasswordInput value={cur} onChange={(e) => setCur(e.target.value)} autoComplete="current-password" placeholder="Lasciala vuota se entri solo con Google" />
          </Field>
          <div className="fields-2">
            <Field label="Nuova password">
              <PasswordInput value={next} onChange={(e) => setNext(e.target.value)} minLength={8} required autoComplete="new-password" />
            </Field>
            <Field label="Ripeti la nuova password">
              <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} minLength={8} required autoComplete="new-password" />
            </Field>
          </div>
          <div className="hstack" style={{ justifyContent: 'flex-end' }}>
            <button type="submit" className="btn dark" disabled={busy}>
              <Icon name="key" size={16} /> {pwDone ? 'Password aggiornata' : 'Aggiorna password'}
            </button>
          </div>
        </form>
        )}

        {/* «Esci» sta solo nel menu con il nome, in alto a destra (23/10/2026, Marco: niente doppioni) */}
      </Panel>
    </div>
  );
}

interface GoogleKeysStatus {
  set: boolean;
  fromEnv: boolean;
  clientIdEnd: string | null;
  redirects: string[];
}

/** Chiavi di Google per tutto lo studio: si incollano una volta sola (servono a Calendar e all'accesso con Google). */
function GoogleKeys({ ctx }: { ctx: Ctx }) {
  const { api } = ctx;
  const [st, setSt] = useState<GoogleKeysStatus | null>(null);
  const [open, setOpen] = useState(false);
  const [id, setId] = useState('');
  const [secret, setSecret] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get<GoogleKeysStatus>('/api/admin/google-keys').then(setSt).catch(() => undefined);
  }, [api]);

  if (!st) return null;

  const copy = async (t: string) => {
    await navigator.clipboard.writeText(t).catch(() => undefined);
    setCopied(t);
    setTimeout(() => setCopied(null), 1500);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      setSt(await api.put<GoogleKeysStatus>('/api/admin/google-keys', { clientId: id.trim(), clientSecret: secret.trim() }));
      setId('');
      setSecret('');
      setOpen(false);
      setSaved(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="card stack" style={{ gap: 6 }}>
      <div className="hstack">
        <div className="eyebrow grow">Chiavi di Google</div>
        {st.set ? (
          <Pill tone="good" icon="check">
            Inserite
          </Pill>
        ) : (
          <Pill tone="warn" icon="key">
            Mancano
          </Pill>
        )}
      </div>
      <div className="muted small">
        Servono una volta sola per tutto lo studio: fanno arrivare visite e call sul Google Calendar di ognuno.
        {st.set && st.clientIdEnd ? ` In uso: ${st.clientIdEnd}` : ''}
      </div>
      {saved && <div className="small">Salvate. Ora tu e Luca collegate il vostro Google Calendar dalla pagina Agenda.</div>}
      {st.fromEnv ? (
        <div className="muted small">Sono scritte nel file del server: si cambiano da lì.</div>
      ) : !open && st.set ? (
        <div className="hstack" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn small" onClick={() => setOpen(true)}>
            <Icon name="edit" size={14} /> Cambia le chiavi
          </button>
        </div>
      ) : (
        <>
          <div className="small">Indirizzi da incollare su Google, in «URI di reindirizzamento autorizzati»:</div>
          {st.redirects.map((r) => (
            <div key={r} className="hstack" style={{ gap: 6 }}>
              <code className="grow small" style={{ wordBreak: 'break-all' }}>
                {r}
              </code>
              <button type="button" className="btn small" onClick={() => void copy(r)}>
                <Icon name={copied === r ? 'check' : 'copy'} size={14} /> {copied === r ? 'Copiato' : 'Copia'}
              </button>
            </div>
          ))}
          <ErrorBanner message={error} onClose={() => setError(null)} />
          <div className="fields-2">
            <Field label="ID client" hint="Finisce con .apps.googleusercontent.com">
              <input value={id} onChange={(e) => setId(e.target.value)} autoComplete="off" spellCheck={false} required />
            </Field>
            <Field label="Client secret" hint="Il codice segreto che Google mostra accanto all'ID">
              <PasswordInput value={secret} onChange={(e) => setSecret(e.target.value)} autoComplete="off" required />
            </Field>
          </div>
          <div className="hstack" style={{ justifyContent: 'flex-end' }}>
            {st.set && (
              <button type="button" className="btn" onClick={() => setOpen(false)} disabled={busy}>
                Annulla
              </button>
            )}
            <button type="submit" className="btn dark" disabled={busy}>
              <Icon name="key" size={16} /> {busy ? 'Salvataggio…' : 'Salva le chiavi'}
            </button>
          </div>
        </>
      )}
    </form>
  );
}

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

/** Dove sei collegato: i dispositivi con il tuo accesso aperto. Uno che non conosci si chiude con un clic. */
function Sessions({ ctx, onPasswordless }: { ctx: Ctx; onPasswordless: (v: boolean) => void }) {
  const { api } = ctx;
  const [data, setData] = useState<AccessSessionsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () =>
    api
      .get<AccessSessionsResponse>('/api/auth/sessions')
      .then((d) => {
        setData(d);
        onPasswordless(d.passwordless);
      })
      .catch((e: Error) => setError(e.message));
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  if (!data) return error ? <ErrorBanner message={error} onClose={() => setError(null)} /> : null;

  const close = async (id: number) => {
    if (!(await ask('Chiudere l’accesso da questo dispositivo? Chi lo sta usando dovrà rientrare.', { title: 'Chiudi l’accesso', ok: 'Chiudi', danger: true }))) return;
    await api.del(`/api/auth/sessions/${id}`).catch((e: Error) => setError(e.message));
    await load();
  };
  const others = data.sessions.filter((s) => !s.current);
  const closeOthers = async () => {
    if (!(await ask(`Chiudere l’accesso su ${others.length === 1 ? 'l’altro dispositivo' : `tutti gli altri ${others.length} dispositivi`}? Resti collegato solo qui.`, { title: 'Esci dagli altri dispositivi', ok: 'Chiudi', danger: true }))) return;
    await api.post('/api/auth/sessions/close-others').catch((e: Error) => setError(e.message));
    await load();
  };

  return (
    <div className="card stack" style={{ gap: 8 }}>
      <div className="eyebrow">Dove sei collegato</div>
      <div className="muted small">I dispositivi con il tuo accesso aperto. Se ne vedi uno che non conosci, chiudilo.</div>
      <ErrorBanner message={error} onClose={() => setError(null)} />
      {data.sessions.map((s) => (
        <div key={s.id} className="hstack" style={{ gap: 10 }}>
          <div className="grow" style={{ minWidth: 0 }}>
            <b>{s.device}</b>
            {s.current && (
              <Pill tone="good" className="small" title="Il dispositivo che stai usando adesso">
                Questo
              </Pill>
            )}
            <div className="muted small">
              {s.ip ? `Indirizzo ${s.ip} · ` : ''}usato {when(s.lastUsedAt)} · aperto il {when(s.createdAt)}
            </div>
          </div>
          {!s.current && (
            <button type="button" className="btn small" title="Chiude l’accesso su quel dispositivo" onClick={() => close(s.id)}>
              Chiudi
            </button>
          )}
        </div>
      ))}
      {others.length > 1 && (
        <div className="hstack" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn small" onClick={closeOthers}>
            Esci da tutti gli altri ({others.length})
          </button>
        </div>
      )}
    </div>
  );
}

/** Account Google o Microsoft con cui entrare senza password. */
function LinkedAccounts({ ctx, passwordless }: { ctx: Ctx; passwordless: boolean }) {
  const { api } = ctx;
  const [sso, setSso] = useState<SsoProvidersDto | null>(null);
  const [list, setList] = useState<SsoIdentityDto[]>([]);
  const [wait, setWait] = useState<SsoProvider | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cancel = useRef(false);

  const load = () => api.get<SsoIdentityDto[]>('/api/auth/identities').then(setList).catch(() => undefined);
  useEffect(() => {
    api.get<SsoProvidersDto>('/api/auth/sso').then(setSso).catch(() => setSso({ google: false, microsoft: false }));
    load();
    return () => {
      cancel.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  if (!sso || (!sso.google && !sso.microsoft && list.length === 0)) return null;

  const link = async (provider: SsoProvider) => {
    cancel.current = false;
    setWait(provider);
    setError(null);
    try {
      const r = await runSso(api, '/api/auth/sso/link', { provider }, () => cancel.current);
      if (r.status === 'error' && r.message) setError(r.message);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setWait(null);
    }
  };

  const remove = async (id: number) => {
    setError(null);
    await api.del(`/api/auth/identities/${id}`).catch((e: Error) => setError(e.message));
    await load();
  };

  return (
    <div className="card stack" style={{ gap: 8 }}>
      <div className="eyebrow">Accesso con Google</div>
      <div className="muted small">
        {passwordless ? 'È il tuo modo per entrare: la password non c\'è, quindi nessuno può entrare indovinandola.' : `Entri con un clic, senza password. Serve solo se il tuo account ha un'email diversa da ${ctx.me.user.email}.`}
      </div>
      <ErrorBanner message={error} onClose={() => setError(null)} />
      {list.map((i) => (
        <div key={i.id} className="hstack" style={{ gap: 10 }}>
          <SsoLogo provider={i.provider} />
          <div className="grow">
            <b>{SSO_LABEL[i.provider]}</b>
            <div className="muted small">{i.email ?? 'account collegato'}</div>
          </div>
          {/* L'unico modo per entrare non si scollega: si resterebbe chiusi fuori */}
          {passwordless && list.length <= 1 ? (
            <Pill tone="good" icon="check">
              Collegato
            </Pill>
          ) : (
            <button type="button" className="btn small" title="Non potrai più entrare con questo account" onClick={() => remove(i.id)}>
              Scollega
            </button>
          )}
        </div>
      ))}
      {wait ? (
        <div className="hstack" style={{ gap: 10 }}>
          <SsoLogo provider={wait} />
          <div className="grow muted small">Scegli l'account {SSO_LABEL[wait]} nel browser…</div>
          <button
            type="button"
            className="btn small"
            onClick={() => {
              cancel.current = true;
              setWait(null);
            }}
          >
            Annulla
          </button>
        </div>
      ) : (
        <div className="hstack wrap" style={{ gap: 8 }}>
          {/* «Collega» solo per i servizi non ancora collegati */}
          {(['google', 'microsoft'] as const)
            .filter((p) => sso[p] && !list.some((i) => i.provider === p))
            .map((p) => (
              <button key={p} type="button" className="btn small sso-btn" title="Collega il tuo account: poi entri con un clic" onClick={() => link(p)}>
                <SsoLogo provider={p} size={15} /> Collega {SSO_LABEL[p]}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
